import React from 'react';
import { Eye, Pen, Trash2 } from 'lucide-react';
import { API_URL } from '../helpers/api';

const EntryList = ({ schemaKey, entries, onEdit, onDelete }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDisplayFields = (schemaKey) => {
    switch (schemaKey) {
      case 'collections':
        return [
          { key: 'mainImage', label: 'Image', type: 'image' },
          { key: 'title', label: 'Titre' },
          { key: 'order', label: 'Ordre' },
          { key: 'updatedAt', label: 'Mis à jour', type: 'date' },
        ];
      case 'events':
        return [
          { key: 'images', label: 'Images', type: 'images' },
          { key: 'title', label: 'Titre' },
          { key: 'newStartDate', label: 'Date de début', type: 'date' },
          { key: 'newEndDate', label: 'Date de fin', type: 'date' },
          { key: 'isFree', label: 'Gratuit', type: 'boolean' },
          { key: 'isPublished', label: 'Publié', type: 'boolean' },
        ];
      case 'hiring-ads':
        return [
          { key: 'image', label: 'Image', type: 'image' },
          { key: 'title', label: 'Titre' },
          { key: 'document', label: 'Documents', type: 'files' },
          { key: 'publishedAt', label: 'Publié le', type: 'date' },
          { key: 'updatedAt', label: 'Mis à jour', type: 'date' },
        ];
      case 'magazines':
        return [
          { key: 'coverImage', label: 'Couverture', type: 'image' },
          { key: 'title', label: 'Titre' },
          { key: 'publicationDate', label: 'Date de publication', type: 'date' },
          { key: 'isPublished', label: 'Publié', type: 'boolean' },
        ];
      case 'patrimoine-actualites':
        return [
          { key: 'images', label: 'Images', type: 'images' },
          { key: 'title', label: 'Titre' },
          { key: 'publishedOn', label: 'Publié le', type: 'date' },
          { key: 'updatedAt', label: 'Mis à jour', type: 'date' },
        ];
      case 'popups':
        return [
          { key: 'mainImage', label: 'Image', type: 'image' },
          { key: 'title', label: 'Titre' },
          { key: 'startDate', label: 'Date de début', type: 'date' },
          { key: 'endDate', label: 'Date de fin', type: 'date' },
          { key: 'active', label: 'Actif', type: 'boolean' },
          { key: 'priority', label: 'Priorité' },
        ];
      default:
        return [
          { key: 'title', label: 'Titre' },
          { key: 'updatedAt', label: 'Mis à jour', type: 'date' },
        ];
    }
  };

  const displayFields = getDisplayFields(schemaKey);

  const renderCellValue = (entry, field) => {
    const value = entry[field.key];
    
    switch (field.type) {
      case 'date':
        return formatDate(value);
      case 'boolean':
        return value ? 
          <span className="badge bg-success">Oui</span> : 
          <span className="badge bg-secondary">Non</span>;
      case 'image':
        return value ? (
          <img 
            src={`${API_URL}${value.url}`} 
            alt={value.name} 
            className="img-thumbnail"
            style={{ width: '40px', height: '40px', objectFit: 'cover' }}
          />
        ) : '-';
      case 'images':
        return value && value.length > 0 ? (
          <div className="d-flex">
            {value.slice(0, 3).map((img, index) => (
              <img 
                key={index}
                src={`${API_URL}${img.url}`} 
                alt={img.name} 
                className="img-thumbnail me-1"
                style={{ width: '30px', height: '30px', objectFit: 'cover' }}
              />
            ))}
            {value.length > 3 && (
              <span className="badge bg-secondary align-self-center">+{value.length - 3}</span>
            )}
          </div>
        ) : '-';
      case 'file':
        return value ? (
          <div className="d-flex align-items-center">
            <span className="badge bg-secondary me-1">📄</span>
            <small className="text-truncate" style={{ maxWidth: '100px' }}>
              {value.name}
            </small>
          </div>
        ) : '-';
      case 'files': {
        const files = Array.isArray(value) ? value : (value ? [value] : []);
        return files.length > 0 ? (
          <div className="d-flex align-items-center">
            <span className="badge bg-secondary me-1">📄</span>
            <small>{files.length} document{files.length > 1 ? 's' : ''}</small>
          </div>
        ) : '-';
      }
      default:
        return value || '-';
    }
  };

  if (entries.length === 0) {
    return (
      <div className="alert alert-info">
        Aucun élément trouvé.
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <table className="table table-striped">
        <thead>
          <tr>
            {displayFields.map(field => (
              <th key={field.key}>{field.label}</th>
            ))}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id}>
              {displayFields.map(field => (
                <td key={field.key}>
                  {renderCellValue(entry, field)}
                </td>
              ))}
              <td>
                <div className="btn-group btn-group-sm">
                  {schemaKey === 'events' && entry.isPublished && (
                    <a
                      href={`/evenement/${entry.documentId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-success"
                      title="Voir sur le site"
                    >
                      <Eye size={20} />
                    </a>
                  )}
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => onEdit(entry)}
                  >
                    <Pen size={20} />
                  </button>
                  <button
                    className="btn btn-outline-danger"
                    onClick={() => onDelete(entry.id)}
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default EntryList;